import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    DialogActions,
    DialogContent,
    Grid,
    styled,
    TextField,
    Typography,
} from "@mui/material";
import { apiEndpoints } from "generated/apiEndpoints";
import FormPreviewFileThumbnail from "components/Form/FormComponents/FormUploadFile/FormPreviewFileThumbnail";
import SidebarInputLabel from "components/sidedrawer/SidebarInputLabel";
import DefaultButton from "components/Button";

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
}));

const StyledBodyBox = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "4px",
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    maxHeight: "300px",
    overflowY: "auto",
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
    padding: `${theme.spacing(0, 3, 3, 3)}`,
    borderTop: 0,
    display: "flex",
    justifyContent: "space-between",
}));

interface DeviationInsuranceCompanyFormProps {
    subjectString: string;
    emailBodyFromObject: string;
    htmlBodyFromObject: string;
    uploadedFiles: any[];
    insuranceCompanyEmails: any[];
    emailObject: Record<string, any>;
    setShowEmailDialog: (show: boolean) => void;
    setShowSendMailResultDialog: (show: boolean) => void;
    sendMailApi: any;
    sendMailResult: any;
    setSentEmailRecipients: (emails: string[]) => void;
    t: any;
}


interface EmailFormValues {
    emailRecipients: any[];
    subject: string;
    body: string;
    uploadedFiles: any[];
}

const DeviationInsuranceCompanyForm: React.FC<
    DeviationInsuranceCompanyFormProps
> = ({
    subjectString,
    emailBodyFromObject,
    htmlBodyFromObject,
    uploadedFiles,
    insuranceCompanyEmails,
    emailObject,
    setShowEmailDialog,
    sendMailApi,
    sendMailResult,
    setSentEmailRecipients,
    t,
}) => {
        return (
            <Formik<EmailFormValues>
                enableReinitialize
                initialValues={{
                    emailRecipients: [],
                    subject: subjectString,
                    body: emailBodyFromObject,
                    uploadedFiles: uploadedFiles?.length ? uploadedFiles : [],
                }}
                validationSchema={Yup.object({
                    emailRecipients: Yup.array()
                        .min(1, t("global.email_is_required", "At least one email is required"))
                        .of(
                            Yup.object({
                                name: Yup.string().required(t("global.name_required", "Name is required")),
                                insuranceCompanyEmail: Yup.array()
                                    .of(
                                        Yup.string()
                                            .trim()
                                            .email(t("global.invalid_email", "Invalid email address"))
                                            .required(t("global.email_is_required", "Email is required"))
                                    )
                                    .min(1, t("global.email_is_required", "Email is required"))
                                    .required(t("global.email_is_required", "Email is required")),
                            })
                        )
                })}

                onSubmit={(values) => {
                    const flattenedEmails = values.emailRecipients
                        .flatMap((recipient) => recipient.insuranceCompanyEmail);

                    const mailData = {
                        to: flattenedEmails.join(', '),
                        subject: values.subject,
                        body: values.body,
                        htmlBody: htmlBodyFromObject,
                        priority: false,
                        attachments: values?.uploadedFiles?.map(file => ({
                            Name: file.fileName,
                            Content: file.Data
                        })) ?? []

                    };
                    sendMailApi.post(apiEndpoints.deviation.senddeviationmail, mailData);
                }}


            >
                {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isValid, dirty }) => (
                    <Form>
                        <StyledDialogContent>
                            <Box>
                                <SidebarInputLabel
                                    labelText={t("global.email_recipients", "Email recipients")}
                                />
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={insuranceCompanyEmails}
                                    getOptionLabel={(option) => {
                                        if (typeof option === "string") return option;
                                        if (Array.isArray(option.name)) {
                                            return option.name.join(", ");
                                        }
                                        return option.name || "";
                                    }}
                                    value={values.emailRecipients}
                                    onChange={(event, value) => {
                                        const normalized = value.map((item) =>
                                            typeof item === "string"
                                                ? {
                                                    name: item,
                                                    insuranceCompanyEmail: [item]
                                                }
                                                : {
                                                    ...item,
                                                    insuranceCompanyEmail: Array.isArray(item.insuranceCompanyEmail)
                                                        ? item.insuranceCompanyEmail
                                                        : [item.insuranceCompanyEmail]
                                                }
                                        );
                                        setSentEmailRecipients(normalized.map(item => item.insuranceCompanyEmail).flat());
                                        setFieldValue("emailRecipients", normalized);
                                    }}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => {
                                            const { key, ...tagProps } = getTagProps({ index });
                                            const label = Array.isArray(option.insuranceCompanyEmail)
                                                ? option.insuranceCompanyEmail.join(" | ")
                                                : option.insuranceCompanyEmail;

                                            return (
                                                <Chip
                                                    key={key}
                                                    label={label}
                                                    {...tagProps}
                                                />
                                            );
                                        })
                                    }
                                    renderInput={(params) => {
                                        const emailError =
                                            touched.emailRecipients &&
                                                errors.emailRecipients &&
                                                typeof errors.emailRecipients === "string"
                                                ? errors.emailRecipients
                                                : null;

                                        return (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                error={Boolean(emailError)}
                                                helperText={emailError}
                                                placeholder={t("global.type_or_select_from_list", "Start typing or select")}
                                            />
                                        );
                                    }}
                                />

                            </Box>
                            { }
                            <Box>
                                <SidebarInputLabel
                                    labelText={t("global.subject", "Subject")}
                                />
                                <TextField
                                    name="subject"
                                    fullWidth
                                    onChange={handleChange}
                                    value={values.subject}
                                    error={touched.subject && errors.subject ? true : false}
                                    helperText={touched.subject && errors.subject ? errors.subject : null}
                                />
                            </Box>

                            <Box>
                                <SidebarInputLabel
                                    labelText={t("deviation.deviation_data", "Deviation data")}
                                />
                                <StyledBodyBox>
                                    {Object.entries(emailObject).length === 0 ? (
                                        <Typography variant="bodyMedium" color="textSecondary">
                                            {t("global.no_data_available", "No data available")}
                                        </Typography>
                                    ) : (
                                        Object.entries(emailObject).map(([key, value]) => (
                                            <Box key={key}>
                                                <Typography variant="bodyMedium" fontWeight={600} component="span">
                                                    {key}:{" "}
                                                </Typography>
                                                <Typography variant="bodyMedium" component="span" color="textSecondary">
                                                    {String(value)}
                                                </Typography>
                                            </Box>
                                        ))
                                    )}

                                    {values?.uploadedFiles && values?.uploadedFiles.length > 0 ? (
                                        <Box mt={3}>
                                            <Typography variant="bodyMedium" fontWeight={600} gutterBottom>
                                                {t("global.attachments", "Attachments")}
                                            </Typography>
                                            <Grid container spacing={2}>
                                                {values?.uploadedFiles?.map((file: any) => (
                                                    <Grid item xs={12} sm={6} md={6} key={file.fileId}>
                                                        <FormPreviewFileThumbnail {...file} />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>
                                    ) : null}
                                </StyledBodyBox>

                                <input type="hidden" name="body" value={values.body} />
                            </Box>
                        </StyledDialogContent>

                        <StyledDialogActions>
                            <Button variant="outlined" onClick={() => setShowEmailDialog(false)}>
                                {t("global.cancel", "Cancel")}
                            </Button>
                            <DefaultButton
                                color="primary"
                                result={sendMailResult}
                                type="submit">
                                {t("global.send", "Send")}
                            </DefaultButton>
                        </StyledDialogActions>
                    </Form>
                )}
            </Formik>
        );
    };

export default DeviationInsuranceCompanyForm;
